import { gql } from 'graphql-request';
import { captureMessage } from '@sentry/nextjs';
import useSWR, { SWRConfiguration } from 'swr';
import { BaseShopFragment, ShopFragment } from '@graphql/fragments';
import { ShopDataProps } from '@typings/product';
import { DefaultVariables, GraphQLRequestProps, request } from '@repo/ui/src/utils/graphql-request';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';

const getShopProducts = gql`
  ${ShopFragment}
  query ShopData($input: ShopProductsInput, $currency: String) {
    products: getShopProducts(input: $input, currency: $currency) {
      ...Shop
    }
  }
`;
const getBaseShopProducts = gql`
  ${BaseShopFragment}
  query BaseShopData($input: ShopProductsInput, $currency: String) {
    products: getShopProducts(input: $input, currency: $currency) {
      ...BaseShop
    }
  }
`;

export const FULL_KEY = 'product_data';
export const BASE_KEY = 'base_product_data';
const EXPIRATION_TIME = 300_000;

interface CachedProduct {
  data: ShopDataProps;
  locale: string;
  expiry: number;
}

const cacheFactory = (KEY: string) => ({
  get() {
    try {
      const data = sessionStorage.getItem(KEY);
      return (JSON.parse(data!) ?? []) as Array<CachedProduct>;
    } catch {
      return [];
    }
  },
  set(value: Array<CachedProduct>) {
    try {
      return sessionStorage.setItem(KEY, JSON.stringify(value));
    } catch (e) {
      /* empty */
    }
  },
});

interface Variables extends DefaultVariables {
  input: {
    skus: Array<string>;
  };
  currency: string;
}

interface Key extends GraphQLRequestProps<Variables> {
  fullData: boolean;
}

const fetcher = async ({ fullData, ...props }: Key) => {
  const skus = props.variables!.input?.skus?.filter(Boolean);
  if (!skus?.length) return [];

  const cache = cacheFactory(fullData ? FULL_KEY : BASE_KEY);
  const cachedProducts = cache.get();
  const now = Date.now();

  const validCachedProducts = cachedProducts.filter(
    (item) => props.locale === item.locale && now < item.expiry,
  );

  const validRelatedCachedProducts = validCachedProducts
    .filter((item) => skus.includes(item.data.id))
    .map(({ data }) => data);

  const validRelatedCachedSkus = validRelatedCachedProducts.map((item) => item.id);
  const missingOrExpiredSkus = skus.filter((sku) => !validRelatedCachedSkus.includes(sku));

  let filteredProducts: ShopDataProps[] = [];

  if (missingOrExpiredSkus.length) {
    const { products } = await request<{ products: Array<ShopDataProps> }>({
      ...props,
      variables: {
        ...props.variables,
        input: { ...(props.variables.input ?? null), skus: missingOrExpiredSkus },
      },
    })!;

    const expiry = Date.now() + EXPIRATION_TIME;

    filteredProducts = products.filter((item) => item?.id);

    const fetchedProducts = filteredProducts.map((data) => ({
      data,
      locale: props.locale,
      expiry,
    }));

    cache.set([...validCachedProducts, ...fetchedProducts]);
  }

  const allProducts: ShopDataProps[] = [...validRelatedCachedProducts, ...filteredProducts];
  const allProductsMap = new Map(allProducts.map((product) => [product.id, product]));

  return skus.map((sku) => allProductsMap.get(sku)!);
};

export const shopDataProductsFetcher = (
  skus: string[],
  locale: string,
  currency: string,
  fullData = false,
) => {
  if (!skus || !skus.length) return [];
  return fetcher({
    document: getShopProducts,
    variables: {
      input: {
        skus,
      },
      currency,
    },
    locale,
    fullData,
  });
};

const useShopDataProducts = (skus: string[], fullData = false, config: SWRConfiguration = {}) => {
  const { locale, currency } = useCurrentUrl();
  const uppercaseSkus = skus?.map((sku) => sku?.toUpperCase());

  return useSWR<ShopDataProps[] | undefined>(
    {
      document: fullData ? getShopProducts : getBaseShopProducts,
      variables: {
        input: {
          skus: uppercaseSkus,
        },
        currency,
      },
      locale,
      fullData,
    },
    fetcher,
    {
      keepPreviousData: true,
      refreshInterval: EXPIRATION_TIME,
      revalidateOnFocus: false,
      onError(e) {
        if (e.message !== 'Network request failed') {
          captureMessage('Could not get products', {
            level: 'error',
            extra: {
              skus,
              response: JSON.stringify(e?.response?.data),
              message: e?.message,
              code: e?.code,
            },
          });
        }
      },
      ...config,
    },
  );
};

export default useShopDataProducts;
